import React, { useId } from 'react';

import Tooltip from 'components/controls/Tooltip';
import { getHighestValidationSeverity, getValidationIssueMessage, getValidationIssuesCount } from 'util/compilerValidation';
import { ValidationSeverity } from 'util/compilerValidation';

import { InformationCircleIcon, StatusAttentionIcon, WarningTriangleIcon } from '.';

const TooltipErrorIcon: React.FunctionComponent<{
  content?: boolean | JSX.Element | string | object;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'mouse' | undefined;
}> = ({ content, position }) => {
  const iconId = useId();

  if (!content || typeof content === 'boolean') {
    return <SeverityIcon severity="error" />;
  }

  let tooltipContent: unknown = content;
  let severity: ValidationSeverity = 'error';

  if (typeof content !== 'boolean' && !React.isValidElement(content)) {
    const issueMessage = getValidationIssueMessage(content);
    if (issueMessage) {
      tooltipContent = issueMessage;
      severity = getHighestValidationSeverity(content);
    } else if (typeof content === 'object') {
      const issuesCount = getValidationIssuesCount(content);
      const issueWord = issuesCount === 1 ? 'issue' : 'issues';
      tooltipContent = issuesCount > 0 ? `Has ${issuesCount} ${issueWord}` : 'Has an issue';
      severity = getHighestValidationSeverity(content);
    }
  }

  if (!React.isValidElement(tooltipContent) && typeof tooltipContent !== 'string' && typeof tooltipContent !== 'number') {
    const issueMessage = getValidationIssueMessage(tooltipContent);
    if (issueMessage) {
      tooltipContent = issueMessage;
    } else {
      const issuesCount = getValidationIssuesCount(tooltipContent);
      const issueWord = issuesCount === 1 ? 'issue' : 'issues';
      tooltipContent = issuesCount > 0 ? `Has ${issuesCount} ${issueWord}` : 'Has an issue';
    }
  }

  const safeTooltipContent: React.ReactNode =
    React.isValidElement(tooltipContent) || typeof tooltipContent === 'string' || typeof tooltipContent === 'number'
      ? tooltipContent
      : 'Has an issue';

  return (
    <>
      <SeverityIcon severity={severity} id={iconId} />
      <Tooltip targetId={iconId} position={position ?? 'bottom'} offset={4}>
        {safeTooltipContent}
      </Tooltip>
    </>
  );
};

export default TooltipErrorIcon;

const SeverityIcon: React.FC<{ severity: ValidationSeverity; id?: string }> = ({ severity, id }) => {
  if (severity === 'error') {
    return <StatusAttentionIcon id={id} />;
  }

  if (severity === 'warning') {
    return <WarningTriangleIcon id={id} />;
  }

  return <InformationCircleIcon id={id} />;
};
